import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Bell, 
  Check, 
  CheckCheck, 
  Layers, 
  FileText, 
  Receipt, 
  AlertTriangle, 
  ShieldCheck, 
  Clock, 
  ExternalLink,
  Trash2,
  Sparkles,
  RefreshCw,
  X,
  ShoppingBag
} from 'lucide-react';
import { auditLogsAPI, workflowAPI, taxesAPI, budgetsAPI, vendorPOsAPI, invoicesAPI, rfxAPI, alertThresholdsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import clsx from 'clsx';

export default function RealTimeNotificationCenter() {
  const { user, activePortal, isSuperAdmin, isTenantAdmin } = useAuth();
  const isAdminUser = isSuperAdmin || isTenantAdmin;
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [readIds, setReadIds] = useState(() => {
    try {
      const saved = localStorage.getItem('erp_read_notifications');
      return saved ? JSON.parse(saved) : [];
    } catch (_) {
      return [];
    }
  });

  const isVendor = activePortal === 'vendor' || user?.type === 'Vendor' || user?.portal_access === 'Vendor Portal';
  const popoverRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch real-time live operational events from the database with strict RBAC security
  const fetchLiveNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const results = [];

      if (isVendor) {
        // ================= VENDOR RBAC NOTIFICATIONS =================
        // 1. Fetch Vendor's Purchase Orders assigned by Client
        try {
          const poRes = await vendorPOsAPI.getAll({ limit: 5 });
          const poData = poRes?.data?.data || [];
          if (Array.isArray(poData)) {
            poData.forEach((po, i) => {
              const status = po.status || 'Issued';
              results.push({
                id: `vendor-po-${po.id || i}`,
                category: 'PURCHASE ORDER',
                title: `PO #${po.po_number || po.id}: Status is ${status}`,
                description: `Purchase order valued at ${po.total_amount ? '£' + po.total_amount : 'allocated spend'}. Issued by client.`,
                timestamp: po.created_at || po.issue_date ? new Date(po.created_at || po.issue_date) : new Date(Date.now() - (i + 1) * 3600000),
                link: `/vendor-pos/${po.id}`,
                severity: status === 'Issued' ? 'warning' : 'info'
              });
            });
          }
        } catch (_) {}

        // 2. Fetch Vendor's Invoices
        try {
          const invRes = await invoicesAPI.getAll({ limit: 5 });
          const invData = invRes?.data?.data || [];
          if (Array.isArray(invData)) {
            invData.forEach((inv, i) => {
              results.push({
                id: `vendor-inv-${inv.id || i}`,
                category: 'INVOICE',
                title: `Invoice #${inv.invoice_number || inv.id}: ${inv.status || 'Submitted'}`,
                description: `Payment pipeline status: ${inv.status || 'Pending Verification'}. Amount: £${inv.total_amount || 0}`,
                timestamp: inv.created_at ? new Date(inv.created_at) : new Date(Date.now() - (i + 1) * 7200000),
                link: '/vendor/finance/invoices',
                severity: inv.status === 'Paid' ? 'success' : inv.status === 'Rejected' ? 'danger' : 'info'
              });
            });
          }
        } catch (_) {}

      } else {
        // ================= CLIENT / ADMIN RBAC NOTIFICATIONS =================
        // 0. Fetch active threshold breaches (Finance, Procurement, Inventory, Security)
        if (user?.is_alerts_enabled !== false) {
          try {
            const alertRes = await alertThresholdsAPI.getActiveBreaches();
            const alertData = alertRes?.data?.data || [];
            if (Array.isArray(alertData)) {
              alertData.forEach(breach => {
                results.push({
                  id: `threshold-${breach.id}`,
                  category: String(breach.category || 'ALERT').toUpperCase(),
                  title: breach.title,
                  description: `${breach.description} (Live: ${breach.current_value} ${breach.unit || ''})`,
                  timestamp: breach.detected_at ? new Date(breach.detected_at) : new Date(),
                  link: breach.action_url || '/settings/system?tab=alerts',
                  severity: breach.severity === 'critical' ? 'danger' : 'warning'
                });
              });
            }
          } catch (_) {}
        }

        // 1. Fetch pending workflow approvals requiring action
        try {
          const appRes = await workflowAPI.getPending?.();
          const appData = appRes?.data?.data || appRes?.data || [];
          if (Array.isArray(appData)) {
            appData.slice(0, 5).forEach((item, i) => {
              results.push({
                id: `approval-${item.id || i}`,
                category: 'APPROVAL',
                title: `Pending Approval: ${item.title || item.document_type || 'Workflow Sign-off'}`,
                description: item.description || `Approval action required for ${item.document_number || item.reference_id || 'document'}`,
                timestamp: item.created_at ? new Date(item.created_at) : new Date(Date.now() - (i + 1) * 3600000),
                link: '/approvals',
                severity: 'warning'
              });
            });
          }
        } catch (_) {}

        // 2. Fetch live audit events (admin only — endpoint requires admin role)
        if (isAdminUser) {
          try {
            const auditRes = await auditLogsAPI.list({ limit: 8 });
            const auditData = auditRes?.data?.data?.rows || auditRes?.data?.data || auditRes?.data || [];
            if (Array.isArray(auditData)) {
              auditData.slice(0, 6).forEach((log, i) => {
                const act = String(log.action || '').toUpperCase();
                const isDanger = act.includes('DELETE') || act.includes('REJECT');
                const isSuccess = act.includes('APPROVE') || act.includes('CREATE') || act.includes('PAY');

                let docLink = '/dashboard';
                const entity = String(log.entity_name || '').toLowerCase();
                if (entity.includes('purchase') || entity.includes('po')) docLink = '/purchase-orders';
                else if (entity.includes('requisition') || entity.includes('req')) docLink = '/requisitions';
                else if (entity.includes('invoice')) docLink = '/finance/accounting/invoices';
                else if (entity.includes('payment')) docLink = '/finance/accounting/payments';
                else if (entity.includes('contract')) docLink = '/settings/contracts';
                else if (entity.includes('tax')) docLink = '/finance/accounting/tax';

                results.push({
                  id: `audit-${log.id || i}`,
                  category: 'AUDIT',
                  title: `${log.user_name || 'System Operator'}: ${log.action || 'Updated'} ${log.entity_name || 'Record'}`,
                  description: log.details?.reason || log.details?.summary || `Transaction recorded for ${log.entity_name || 'ledger entity'}`,
                  timestamp: log.created_at ? new Date(log.created_at) : new Date(Date.now() - (i + 2) * 1800000),
                  link: docLink,
                  severity: isDanger ? 'danger' : isSuccess ? 'success' : 'info'
                });
              });
            }
          } catch (_) {}
        }
      }

      // Sort newest first
      results.sort((a, b) => b.timestamp - a.timestamp);
      setNotifications(results);
    } catch (e) {
      console.error('Failed to load real-time notifications', e);
    } finally {
      setLoading(false);
    }
  }, [isVendor]);

  useEffect(() => {
    fetchLiveNotifications();
    const interval = setInterval(fetchLiveNotifications, 45000);
    return () => clearInterval(interval);
  }, [fetchLiveNotifications]);

  const unreadCount = useMemo(() => {
    return notifications.filter(n => !readIds.includes(n.id)).length;
  }, [notifications, readIds]);

  const markAllAsRead = () => {
    const allIds = notifications.map(n => n.id);
    const updated = Array.from(new Set([...readIds, ...allIds]));
    setReadIds(updated);
    localStorage.setItem('erp_read_notifications', JSON.stringify(updated));
  };

  const markAsRead = (id, link) => {
    if (!readIds.includes(id)) {
      const updated = [...readIds, id];
      setReadIds(updated);
      localStorage.setItem('erp_read_notifications', JSON.stringify(updated));
    }
    if (link) {
      setIsOpen(false);
      navigate(link);
    }
  };

  const filteredNotifications = useMemo(() => {
    if (activeFilter === 'ALL') return notifications;
    if (activeFilter === 'UNREAD') return notifications.filter(n => !readIds.includes(n.id));
    return notifications.filter(n => n.category === activeFilter);
  }, [notifications, activeFilter, readIds]);

  return (
    <div className="relative" ref={popoverRef}>
      
      {/* Bell Button with Real Unread Badge */}
      <button
        type="button"
        onClick={() => {
          setIsOpen(prev => !prev);
          if (!isOpen) fetchLiveNotifications();
        }}
        className={clsx(
          "p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors relative cursor-pointer",
          isOpen && "bg-slate-800 text-white ring-1 ring-slate-700"
        )}
        title={isVendor ? "Vendor Operational Notifications" : "Live System Notifications"}
        aria-label="Open notifications"
      >
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[15px] h-[15px] px-1 bg-rose-500 text-white font-extrabold text-[9px] rounded-full flex items-center justify-center ring-2 ring-[#0f172a] shadow-xs">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Flyout Notification Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white border border-slate-200 rounded-2xl shadow-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
          
          {/* Header */}
          <div className="px-4 py-3 bg-slate-50/90 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={clsx(
                "w-6 h-6 rounded-md text-white flex items-center justify-center shadow-xs",
                isVendor ? "bg-emerald-600" : "bg-blue-600"
              )}>
                <Bell className="w-3.5 h-3.5" />
              </div>
              <div>
                <h3 className="text-xs font-black text-slate-900 leading-tight">
                  {isVendor ? "Vendor Notifications" : "Live Notifications"}
                </h3>
                <p className="text-[10px] text-slate-500 font-medium">
                  {isVendor ? "Supplier Operational Feed" : "System Live Event Feed"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={fetchLiveNotifications}
                disabled={loading}
                className="p-1 rounded text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
                title="Refresh feed"
              >
                <RefreshCw className={clsx("w-3 h-3", loading && "animate-spin text-blue-600")} />
              </button>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={markAllAsRead}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200/80 transition-colors cursor-pointer"
                  title="Mark all as read"
                >
                  <CheckCheck className="w-3 h-3" />
                  <span>Mark all read</span>
                </button>
              )}
            </div>
          </div>

          {/* Filter Pills */}
          <div className="px-3 py-1.5 bg-slate-100/70 border-b border-slate-200/70 flex items-center gap-1 text-[10px] font-bold overflow-x-auto stylish-scrollbar">
            {[
              { id: 'ALL', label: `All (${notifications.length})` },
              { id: 'UNREAD', label: `Unread (${unreadCount})` },
              ...(isVendor ? [
                { id: 'PURCHASE ORDER', label: 'Orders' },
                { id: 'INVOICE', label: 'Invoices' }
              ] : [
                { id: 'APPROVAL', label: 'Approvals' },
                { id: 'AUDIT', label: 'Audit Logs' }
              ])
            ].map(f => (
              <button
                key={f.id}
                type="button"
                onClick={() => setActiveFilter(f.id)}
                className={clsx(
                  "px-2 py-0.5 rounded-md transition-all cursor-pointer whitespace-nowrap",
                  activeFilter === f.id
                    ? "bg-white text-blue-700 shadow-2xs font-extrabold"
                    : "text-slate-600 hover:text-slate-900"
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Notifications Scrollable List */}
          <div className="max-h-80 overflow-y-auto stylish-scrollbar divide-y divide-slate-100">
            {loading && notifications.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-400 font-medium">
                <RefreshCw className="w-4 h-4 animate-spin mx-auto mb-2 text-blue-600" />
                <span>Loading live events from database...</span>
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="py-8 px-4 text-center">
                <ShieldCheck className="w-8 h-8 text-slate-300 mx-auto mb-1.5" />
                <p className="text-xs font-bold text-slate-700">All caught up!</p>
                <p className="text-[11px] text-slate-400 mt-0.5">No pending alerts or notifications in this view.</p>
              </div>
            ) : (
              filteredNotifications.map((item) => {
                const isUnread = !readIds.includes(item.id);
                let timeStr = 'Recently';
                try {
                  timeStr = formatDistanceToNow(item.timestamp, { addSuffix: true });
                } catch (_) {}

                return (
                  <div
                    key={item.id}
                    onClick={() => markAsRead(item.id, item.link)}
                    className={clsx(
                      "p-3 hover:bg-slate-50 transition-colors cursor-pointer flex items-start gap-2.5 group",
                      isUnread ? "bg-blue-50/40" : "bg-white"
                    )}
                  >
                    {/* Unread indicator dot */}
                    <div className="mt-1 shrink-0">
                      <span className={clsx(
                        "block w-2 h-2 rounded-full",
                        isUnread ? "bg-blue-600 ring-2 ring-blue-200" : "bg-transparent"
                      )} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1 mb-0.5">
                        <span className={clsx(
                          "text-[9px] font-extrabold uppercase px-1.5 py-0.2 rounded border",
                          item.category === 'PURCHASE ORDER' ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                          item.category === 'APPROVAL' ? "bg-amber-50 text-amber-800 border-amber-200" :
                          item.severity === 'danger' ? "bg-rose-50 text-rose-700 border-rose-200" :
                          "bg-slate-100 text-slate-700 border-slate-200"
                        )}>
                          {item.category}
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono flex items-center gap-0.5">
                          <Clock className="w-2.5 h-2.5" />
                          {timeStr}
                        </span>
                      </div>

                      <h4 className="text-xs font-bold text-slate-900 line-clamp-1 group-hover:text-blue-600 transition-colors">
                        {item.title}
                      </h4>

                      <p className="text-[11px] text-slate-600 line-clamp-2 mt-0.5 leading-snug">
                        {item.description}
                      </p>
                    </div>

                    <ExternalLink className="w-3 h-3 text-slate-300 group-hover:text-blue-600 shrink-0 mt-1 transition-colors" />
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-500 font-medium">
            <span>{isVendor ? "Vendor Telemetry" : "MIREVA Event Dispatcher"}</span>
            <Link 
              to={isVendor ? "/vendor-pos" : "/approvals"} 
              onClick={() => setIsOpen(false)}
              className="text-blue-600 hover:text-blue-800 font-bold"
            >
              {isVendor ? "View All POs →" : "Approval Inbox →"}
            </Link>
          </div>

        </div>
      )}

    </div>
  );
}
